import type { Media, Page } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'

export const LANDING_HERO_FALLBACK_IMAGE = '/media/homepage/landing.png'

export type CmsUpload = number | Media | null | undefined

export type ResolvedHeroBackground = {
  imageSrc: string
  imageAlt: string
  desktopVideoSrc?: string
  mobileVideoSrc?: string
}

export type ResolveHeroBackgroundMediaInput = {
  cmsImage?: CmsUpload
  cmsVideoDesktop?: CmsUpload
  cmsVideoMobile?: CmsUpload
  fallbackImage: string
  fallbackImageAlt?: string
  fallbackVideoDesktop?: string
  fallbackVideoMobile?: string
}

export function resolveMediaUrl(media: CmsUpload): string | undefined {
  if (!media || typeof media === 'number') {
    return undefined
  }

  const url = getMediaUrl(media.url)
  return url || undefined
}

export function resolveMediaAlt(media: CmsUpload): string | undefined {
  if (!media || typeof media === 'number') {
    return undefined
  }

  const alt = media.alt?.trim()
  return alt || undefined
}

/**
 * CMS video wins when present. A CMS image with no video is image-only.
 * Missing CMS media keeps the caller’s static defaults. Missing mobile video
 * falls back to the desktop video (and the reverse when only mobile is set).
 */
export function resolveHeroBackgroundMedia({
  cmsImage,
  cmsVideoDesktop,
  cmsVideoMobile,
  fallbackImage,
  fallbackImageAlt = '',
  fallbackVideoDesktop,
  fallbackVideoMobile,
}: ResolveHeroBackgroundMediaInput): ResolvedHeroBackground {
  const cmsImageSrc = resolveMediaUrl(cmsImage)
  const cmsImageAlt = resolveMediaAlt(cmsImage)
  const cmsDesktopVideo = resolveMediaUrl(cmsVideoDesktop)
  const cmsMobileVideo = resolveMediaUrl(cmsVideoMobile)
  const cmsVideo = cmsDesktopVideo || cmsMobileVideo

  const imageSrc = cmsImageSrc ?? fallbackImage
  const imageAlt = cmsImageAlt ?? fallbackImageAlt

  if (cmsVideo) {
    return {
      imageSrc,
      imageAlt,
      desktopVideoSrc: cmsDesktopVideo || cmsMobileVideo,
      mobileVideoSrc: cmsMobileVideo || cmsDesktopVideo,
    }
  }

  if (cmsImageSrc) {
    return { imageSrc, imageAlt }
  }

  const fallbackDesktop = fallbackVideoDesktop || fallbackVideoMobile
  if (fallbackDesktop) {
    return {
      imageSrc,
      imageAlt,
      desktopVideoSrc: fallbackVideoDesktop || fallbackVideoMobile,
      mobileVideoSrc: fallbackVideoMobile || fallbackVideoDesktop,
    }
  }

  return { imageSrc, imageAlt }
}

export function resolveLandingHeroBackground(
  landingHero: Page['landingHero'] | null | undefined,
): ResolvedHeroBackground {
  return resolveHeroBackgroundMedia({
    cmsImage: landingHero?.image,
    cmsVideoDesktop: landingHero?.video,
    cmsVideoMobile: landingHero?.videoMobile,
    fallbackImage: LANDING_HERO_FALLBACK_IMAGE,
  })
}
